import { XIcon } from '@phosphor-icons/react'

import { Icon } from '@/components/ui/icon'
import { Overlay } from '@/components/ui/overlay'
import { Typography } from '@/components/ui/typography'
import { useIsMobile } from '@/hooks'

import * as S from './modal.styled'
import { ModalProps } from './modal.types'

export const Modal = ({
  children,
  onClose,
  title,
  headerIcon,
  footer,
  closeable = true,
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  autoWidth,
  height,
  width,
  bodyPaddingTop,
  onBeforeClose,
  size = 'medium',
  overlayStyle,
  style,
  ...props
}: ModalProps) => {
  const isMobile = useIsMobile()

  // Note: a caller-provided inline `style` (e.g. TimeBlockNotesModal's fixed maxHeight/
  // minHeight) beats any plain CSS selector, including ModalContent's mobile full-screen
  // media query (modal.styled.ts) - strip the sizing overrides on mobile instead of
  // reaching for !important.
  const mobileSafeStyle = isMobile
    ? { ...style, height: undefined, maxHeight: undefined, minHeight: undefined, width: undefined }
    : style

  return (
    <Overlay
      closeable={closeable}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEscapeKey={closeOnEscapeKey}
      style={overlayStyle}
      onBeforeClose={onBeforeClose}
      onClose={onClose}
    >
      {({ handleClose, getFloatingProps, setFloating }) => (
        <S.ModalContent
          ref={setFloating}
          $size={size}
          $height={height}
          $width={width}
          $autoWidth={autoWidth}
          {...getFloatingProps()}
          {...props}
          style={mobileSafeStyle}
        >
          <S.ModalHeader>
            <S.ModalHeaderTitle>
              {headerIcon && (
                <S.ModalHeaderIcon aria-hidden="true">
                  <Icon icon={headerIcon} size="2.4rem" />
                </S.ModalHeaderIcon>
              )}
              <Typography as="h2" size="text-huge" color="black800">
                {title}
              </Typography>
            </S.ModalHeaderTitle>
            <S.ModalCloseButton
              type="button"
              aria-label="Close modal"
              disabled={!closeable}
              onClick={handleClose}
            >
              <Icon icon={XIcon} size="2rem" />
            </S.ModalCloseButton>
          </S.ModalHeader>
          <S.ModalBody>
            <S.ModalBodyContent $paddingTop={bodyPaddingTop}>{children}</S.ModalBodyContent>
          </S.ModalBody>
          {footer && <S.ModalFooter>{footer}</S.ModalFooter>}
        </S.ModalContent>
      )}
    </Overlay>
  )
}
